// src/features/tuition/TuitionLayer.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Fab,
  CircularProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as StudentsIcon,
  Schedule as ScheduleIcon,
  AttachMoney as FinanceIcon,
  Book as ResourcesIcon,
  Assessment as ReportsIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  School as OnlineLearningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { THEME_COLORS } from '../../styles/theme';

// Import components
import DashboardOverview from './components/dashboard/DashboardOverview';
import StudentManagement from './components/students/StudentManagement';
import SchedulingCalendar from './components/scheduling/SchedulingCalendar';
import FinanceManagement from './components/finances/FinanceManagement';
import OnlineLearning from './components/online/OnlineLearning';


const TuitionLayer = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  
  useEffect(() => {
    // Initialize data
    if (currentUser) {
      // Load any necessary initial data here
      setLoading(false);
    }
  }, [currentUser]);
  
  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };
  
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress sx={{ color: THEME_COLORS.primary }} />
        </Box>
      );
    }
    
    switch (activeSection) {
      case 'dashboard':
        return <DashboardOverview userId={currentUser?.uid} />;
      case 'students':
        return <StudentManagement userId={currentUser?.uid} />;
      case 'scheduling':
        return <SchedulingCalendar userId={currentUser?.uid} />;
      case 'finances':
        return <FinanceManagement userId={currentUser?.uid} />;
      case 'onlineLearning':
        return <OnlineLearning userId={currentUser?.uid} />;
      case 'resources':
        return <ResourcesSection userId={currentUser?.uid} />;
      case 'reports':
        return <ReportsSection userId={currentUser?.uid} />;
      default:
        return <DashboardOverview userId={currentUser?.uid} />;
    }
  };
  
  const drawerWidth = 280;
  
  const menu = (
    <List>
      <ListItem sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
          Tuition Management
        </Typography>
      </ListItem>
      <Divider sx={{ mb: 2 }} />
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'dashboard'}
          onClick={() => handleSectionChange('dashboard')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <DashboardIcon sx={{ color: activeSection === 'dashboard' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Dashboard" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'dashboard' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'students'}
          onClick={() => handleSectionChange('students')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <StudentsIcon sx={{ color: activeSection === 'students' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Students" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'students' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'scheduling'}
          onClick={() => handleSectionChange('scheduling')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <ScheduleIcon sx={{ color: activeSection === 'scheduling' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Scheduling" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'scheduling' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'finances'}
          onClick={() => handleSectionChange('finances')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <FinanceIcon sx={{ color: activeSection === 'finances' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Finances" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'finances' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      
      {/* New Online Learning Section */}
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'onlineLearning'}
          onClick={() => handleSectionChange('onlineLearning')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <OnlineLearningIcon sx={{ color: activeSection === 'onlineLearning' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Online Learning" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'onlineLearning' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'resources'}
          onClick={() => handleSectionChange('resources')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <ResourcesIcon sx={{ color: activeSection === 'resources' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Resources" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'resources' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton
          selected={activeSection === 'reports'}
          onClick={() => handleSectionChange('reports')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <ReportsIcon sx={{ color: activeSection === 'reports' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Reports" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'reports' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
    </List>
  );
  
  return (
    <Container 
      maxWidth="lg" 
      sx={{ mt: 0, mb: 2, p: { xs: 0, sm: 2 } }} 
      disableGutters={isMobile}
    >
      <Box sx={{ display: 'flex' }}>
        {/* Sidebar for non-mobile */}
        {!isMobile && (
          <Box
            component="nav"
            sx={{
              width: { sm: drawerWidth },
              flexShrink: { sm: 0 },
              transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                width: drawerOpen ? drawerWidth : 72,
                maxHeight: '100%',
                p: 2,
                overflow: 'hidden',
                borderRadius: 2,
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }}
            >
              {drawerOpen ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
                    Tuition
                  </Typography>
                  <IconButton onClick={toggleDrawer}>
                    <ChevronLeftIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" mb={2}>
                  <IconButton onClick={toggleDrawer}>
                    <MenuIcon />
                  </IconButton>
                </Box>
              )}
              
              <Divider sx={{ mb: 2 }} />
              
              <List>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'dashboard'}
                    onClick={() => handleSectionChange('dashboard')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <DashboardIcon sx={{ color: activeSection === 'dashboard' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Dashboard" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'dashboard' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'students'}
                    onClick={() => handleSectionChange('students')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <StudentsIcon sx={{ color: activeSection === 'students' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Students" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'students' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'scheduling'}
                    onClick={() => handleSectionChange('scheduling')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <ScheduleIcon sx={{ color: activeSection === 'scheduling' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Scheduling" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'scheduling' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'finances'}
                    onClick={() => handleSectionChange('finances')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <FinanceIcon sx={{ color: activeSection === 'finances' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Finances" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'finances' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                
                {/* Online Learning Nav Item */}
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'onlineLearning'}
                    onClick={() => handleSectionChange('onlineLearning')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <OnlineLearningIcon sx={{ color: activeSection === 'onlineLearning' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Online Learning" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'onlineLearning' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'resources'}
                    onClick={() => handleSectionChange('resources')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <ResourcesIcon sx={{ color: activeSection === 'resources' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Resources" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'resources' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={activeSection === 'reports'}
                    onClick={() => handleSectionChange('reports')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <ReportsIcon sx={{ color: activeSection === 'reports' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Reports" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'reports' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              </List>
            </Paper>
          </Box>
        )}
        
        {/* Mobile drawer */}
        {isMobile && (
          <>
            <Drawer
              variant="temporary"
              open={drawerOpen}
              onClose={toggleDrawer}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile
              }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                  p: 2
                },
              }}
            >
              {menu}
            </Drawer>
            
            <Fab
              color="primary"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1100,
                bgcolor: THEME_COLORS.primary,
                '&:hover': {
                  bgcolor: '#1e1c4e'
                }
              }}
            >
              <MenuIcon />
            </Fab>
          </>
        )}
        
        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 0 },
            pl: { sm: 3 },
            width: { xs: '100%', sm: `calc(100% - ${drawerOpen ? drawerWidth : 72}px)` },
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            minWidth: 0
          }}
        >
          {/* Main content area */}
          <Box sx={{ width: '100%', flexGrow: 1 }}>
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

// Placeholder components - these would be in their own files in a real app
const ResourcesSection = () => (
  <Box p={4} textAlign="center">
    <Typography variant="h6">Resources</Typography>
    <Typography color="textSecondary">Coming soon</Typography>
  </Box>
);

const ReportsSection = () => (
  <Box p={4} textAlign="center">
    <Typography variant="h6">Reports</Typography>
    <Typography color="textSecondary">Coming soon</Typography>
  </Box>
);

export default TuitionLayer;